import type { LandingBlock, LandingBlockType, LandingVariant } from '@/types'

export const uid = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10)

export interface BlockMeta {
  type: LandingBlockType
  label: string
  description: string
}

export const BLOCK_META: BlockMeta[] = [
  { type: 'hero', label: 'Hero', description: 'Título principal + CTA' },
  { type: 'features', label: 'Beneficios', description: 'Tarjetas de características' },
  { type: 'social_proof', label: 'Prueba social', description: 'Métricas y testimonios' },
  { type: 'cta', label: 'Llamado a la acción', description: 'Bloque CTA destacado' },
  { type: 'faq', label: 'Preguntas frecuentes', description: 'Lista de preguntas' },
  { type: 'form', label: 'Formulario', description: 'Captura de leads' },
  { type: 'rich_text', label: 'Texto enriquecido', description: 'HTML libre con estilos base' },
  {
    type: 'custom_html',
    label: 'HTML / CSS personalizado',
    description: 'Bloque totalmente libre',
  },
]

export const BLOCK_LABELS: Record<string, string> = Object.fromEntries(
  BLOCK_META.map((b) => [b.type, b.label])
)

export function makeBlock(type: LandingBlockType): LandingBlock {
  const base: LandingBlock = { id: uid(), type, visible: true, data: {} }
  switch (type) {
    case 'hero':
      return {
        ...base,
        data: {
          eyebrow: 'Nuevo',
          title: 'Tu titular que convierte',
          subtitle: 'Explica en una línea el valor de tu producto.',
          cta_label: 'Empezar gratis',
          cta_href: '#form',
          image: '',
          badge: '',
        },
      }
    case 'features':
      return {
        ...base,
        data: {
          title: 'Por qué elegirnos',
          items: [
            { icon: '⚡', title: 'Rápido', text: 'Resultados en minutos.' },
            { icon: '🔒', title: 'Seguro', text: 'Tus datos protegidos.' },
            { icon: '📈', title: 'Escalable', text: 'Crece sin límites.' },
          ],
        },
      }
    case 'social_proof':
      return {
        ...base,
        data: {
          title: 'Miles ya confían en nosotros',
          stats: [
            { value: '+10.000', label: 'usuarios' },
            { value: '4.9/5', label: 'valoración' },
          ],
          testimonials: [
            {
              quote: 'Cambió la forma en que trabajamos.',
              author: 'Ana López',
              role: 'CMO',
              avatar: '',
            },
          ],
        },
      }
    case 'cta':
      return {
        ...base,
        data: {
          title: '¿Listo para empezar?',
          subtitle: 'Únete hoy y obtén acceso inmediato.',
          cta_label: 'Crear cuenta',
          cta_href: '#form',
        },
      }
    case 'faq':
      return {
        ...base,
        data: {
          title: 'Preguntas frecuentes',
          items: [
            { q: '¿Cómo funciona?', a: 'Te registras y empiezas en minutos.' },
            { q: '¿Puedo cancelar?', a: 'Sí, cuando quieras.' },
          ],
        },
      }
    case 'form':
      return {
        ...base,
        data: {
          title: 'Solicita una demo',
          subtitle: 'Déjanos tus datos y te contactamos.',
          submit_label: 'Quiero la demo',
          success_message: '¡Gracias! Te contactaremos muy pronto.',
          fields: [
            { label: 'Nombre', name: 'name', type: 'text', required: true },
            { label: 'Email', name: 'email', type: 'email', required: true },
          ],
        },
      }
    case 'rich_text':
      return {
        ...base,
        data: { html: '<h2 class="lp-h2">Tu título</h2><p class="lp-muted">Tu texto aquí.</p>' },
      }
    case 'custom_html':
      return {
        ...base,
        custom_html:
          '<div style="padding:48px;text-align:center">\n  <h2>Bloque libre</h2>\n  <p>Escribe HTML y CSS a tu gusto.</p>\n</div>',
        custom_css: '',
        data: {},
      }
    default:
      return base
  }
}

/** Deep-copy a block list, regenerating block ids so keys never collide across variants. */
export function cloneBlocks(blocks: LandingBlock[]): LandingBlock[] {
  const copy = JSON.parse(JSON.stringify(blocks ?? [])) as LandingBlock[]
  return copy.map((b) => ({ ...b, id: uid() }))
}

const LETTERS = 'abcdefghijklmnopqrstuvwxyz'

/** Next free single-letter id (a, b, c…) not already used by a variant. */
export function nextVariantId(existing: LandingVariant[]): string {
  const used = new Set((existing ?? []).map((v) => v.id))
  for (const c of LETTERS) {
    if (!used.has(c)) return c
  }
  return uid()
}

export function makeVariant(id: string, blocks: LandingBlock[]): LandingVariant {
  return { id, name: `Variante ${id.toUpperCase()}`, weight: 1, blocks: cloneBlocks(blocks) }
}
